import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { TechniqueKey } from "../types";
import { SONIOX_VOICES } from "../lib/schemaModes";
import { createChairEngine } from "../lib/chairAudioEngine";
import { postChair } from "../api";
import { useToast } from "../../../components/Toast";
import { PlaybackControls } from "./PlaybackControls";

interface SingleVoiceToolProps {
  patientId: string;
  sessionId?: string;
  language: "en" | "hu";
  getToken: () => Promise<string | null>;
  technique: TechniqueKey;   // sent as the clip's mode, for billing/traceability
  textLabelKey: string;      // e.g. "experiential.letterText"
  defaultVoice: string;
}

export function SingleVoiceTool({
  patientId, sessionId, language, getToken, technique, textLabelKey, defaultVoice,
}: SingleVoiceToolProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [text, setText] = useState("");
  const [voice, setVoice] = useState(defaultVoice);
  const [busy, setBusy] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [looping, setLooping] = useState(false);

  const engineRef = useRef<ReturnType<typeof createChairEngine> | null>(null);

  const onGenerate = async () => {
    try {
      setBusy(true);
      await engineRef.current?.close();
      const res = await postChair(getToken, {
        patient_id: patientId,
        session_id: sessionId,
        language,
        chairs: [{ id: crypto.randomUUID(), mode: technique, label: "", text, voice }],
      });
      const engine = createChairEngine(res.clips);
      await engine.load((url) => fetch(url).then((r) => r.arrayBuffer()));
      engineRef.current = engine;
      setGenerated(true);
    } catch (e) {
      toast.error(`${t("experiential.genError")}: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    return () => {
      engineRef.current?.close();
    };
  }, []);

  return (
    <div>
      <TextField
        label={t(textLabelKey)}
        multiline
        minRows={4}
        fullWidth
        size="small"
        value={text}
        onChange={(e) => setText(e.target.value)}
        sx={{ mb: 2 }}
      />
      <FormControl size="small" sx={{ minWidth: 130, mb: 2 }}>
        <InputLabel>{t("experiential.readBy")}</InputLabel>
        <Select value={voice} label={t("experiential.readBy")} onChange={(e) => setVoice(e.target.value)}>
          {SONIOX_VOICES.map((v) => (
            <MenuItem key={v} value={v}>{v}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <div className="exp-chair-controls">
        <Button variant="contained" size="small" onClick={onGenerate} disabled={busy || !text.trim()}>
          {t("experiential.generate")}
        </Button>

        {generated && (
          <PlaybackControls
            onPlay={() => engineRef.current?.play()}
            onStop={() => engineRef.current?.stop()}
            looping={looping}
            onToggleLoop={() => {
              const next = !looping;
              setLooping(next);
              engineRef.current?.setLoop(next);
            }}
          />
        )}
      </div>
    </div>
  );
}
